import * as pc from 'playcanvas';
import type { RegionColorEffect } from './splat-region-effects';

/**
 * Cutaway: hide every splat above (or below) a horizontal plane in scene space.
 *
 * Visitors toggle it from the toolbar and drag a vertical slider, so the plane
 * height lives in a uniform instead of the shader source. Moving the slider
 * never recompiles; only toggling the mode installs or removes the effect.
 * Splats shrink to nothing across a small feather band so the cut edge stays soft.
 */
export type CutawayDirection = 'above' | 'below';

export interface CutawayState {
    /** Plane height in scene units (scene-content space, Y up). */
    height: number;
    /** Which side of the plane disappears. */
    direction: CutawayDirection;
    /** Half-width of the soft edge in scene units. */
    feather: number;
}

export const CUTAWAY_KEY = 'cutaway';
export const CUTAWAY_UNIFORM = 'uCutaway';

/** Pack the state into the vec4 the shader reads: height, side sign, feather, enabled. */
export function writeCutawayUniform(target: Float32Array, state: CutawayState): Float32Array {
    target[0] = Number.isFinite(state.height) ? state.height : 0;
    target[1] = state.direction === 'below' ? -1 : 1;
    target[2] = Math.max(1e-4, Number.isFinite(state.feather) ? state.feather : 0);
    target[3] = 1;
    return target;
}

const GLSL = `uniform vec4 ${CUTAWAY_UNIFORM};
void region_${CUTAWAY_KEY}_scale(vec3 sceneCenter, inout vec3 scale) {
    float signedDistance = (sceneCenter.y - ${CUTAWAY_UNIFORM}.x) * ${CUTAWAY_UNIFORM}.y;
    float keep = 1.0 - smoothstep(-${CUTAWAY_UNIFORM}.z, ${CUTAWAY_UNIFORM}.z, signedDistance);
    scale *= keep;
}
`;
const WGSL = `uniform ${CUTAWAY_UNIFORM}: vec4f;
fn region_${CUTAWAY_KEY}_scale(sceneCenter: vec3f, scale: ptr<function, vec3f>) {
    let signedDistance = (sceneCenter.y - uniform.${CUTAWAY_UNIFORM}.x) * uniform.${CUTAWAY_UNIFORM}.y;
    let keep = 1.0 - smoothstep(-uniform.${CUTAWAY_UNIFORM}.z, uniform.${CUTAWAY_UNIFORM}.z, signedDistance);
    *scale = (*scale) * keep;
}
`;

/** The effect is static; the mutable uniform array is what the slider drives. */
export function buildCutawayEffect(uniform: Float32Array): RegionColorEffect {
    return { key: CUTAWAY_KEY, scaleGlsl: GLSL, scaleWgsl: WGSL, bounds: [], uniforms: { [CUTAWAY_UNIFORM]: uniform } };
}

/**
 * Vertical extent of a local-space box after `transform` (splat local → scene
 * content space). Rotated scans need all eight corners, not just min/max.
 */
export function sceneHeightRange(min: pc.Vec3, max: pc.Vec3, transform: pc.Mat4): { min: number; max: number } {
    let lo = Infinity;
    let hi = -Infinity;
    const corner = new pc.Vec3();
    for (let i = 0; i < 8; i++) {
        corner.set(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z);
        const y = transform.transformPoint(corner, corner).y;
        if (y < lo) lo = y;
        if (y > hi) hi = y;
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { min: 0, max: 0 };
    return { min: lo, max: hi };
}

/** Slider position 0..1 (bottom..top) to a plane height inside the range, and back. */
export function cutawayLevelToHeight(level: number, range: { min: number; max: number }): number {
    const t = Math.min(1, Math.max(0, Number.isFinite(level) ? level : 1));
    return range.min + (range.max - range.min) * t;
}
export function cutawayHeightToLevel(height: number, range: { min: number; max: number }): number {
    const span = range.max - range.min;
    if (!(span > 0)) return 1;
    return Math.min(1, Math.max(0, (height - range.min) / span));
}
